import type { EventBus } from "../core/EventBus.ts";
import type {
  MetaState,
  Achievement,
  AchievementId,
  HighscoreEntry,
  RunEnding,
} from "../core/MetaState.ts";
import type { OperatorId } from "../core/RunState.ts";
import type { MetaStateManager } from "../persistence/MetaStateManager.ts";
import type { AchievementTracker } from "../systems/AchievementTracker.ts";
import type { LeaderboardService } from "../persistence/LeaderboardService.ts";
import { getModuleByAchievement } from "../systems/ModuleRegistry.ts";
import { getOperatorUnlockThreshold } from "../systems/OperatorEffects.ts";
import { GAME } from "../core/config.ts";
import type { ThreatLevelId } from "../core/MetaState.ts";

export interface RunEndStats {
  score: number;
  wave: number;
  operator: OperatorId;
  maxCombo: number;
  leaks: number;
  livesAtEnd: number;
  livesBonus: number;
  /** S130 (Story Bible §4): true iff the campaign was won this run — a wave-35
   *  victory OR an endless (Echo Mode) run, which by definition was won first. */
  won: boolean;
  /** The threat level this run was played on — the score's comparison key
   *  (owner, 2026-08-16). Carried from `PreRunSelection.threatLevel`, the same
   *  value `startRun` announces on `run.started`. */
  threatLevel: ThreatLevelId;
}

export interface RunEndResult {
  newAchievements: AchievementId[];
  newModules: string[];
  isHighscore: boolean;
}

/**
 * Handles MetaState updates at run end.
 * - Collects pending achievement unlocks
 * - Updates highscores
 * - Increments veteran counter
 * - Persists MetaState
 * - Emits events for UI
 */
export async function handleRunEnd(
  stats: RunEndStats,
  tracker: AchievementTracker,
  metaState: MetaState,
  manager: MetaStateManager,
  bus: EventBus,
  leaderboardService?: LeaderboardService
): Promise<RunEndResult> {
  const result: RunEndResult = {
    newAchievements: [],
    newModules: [],
    isHighscore: false,
  };

  // --- Evaluate achievements ---
  const veteranAch = metaState.achievements.find((a) => a.id === "veteran");
  const cumulativeRuns = veteranAch?.progress ?? 0;

  const cardAch = metaState.achievements.find((a) => a.id === "card_collector");
  const cumulativeCards = cardAch?.progress ?? 0;

  const skillAch = metaState.achievements.find((a) => a.id === "skill_seeker");
  // Reconstruct cumulative unique skills from progress (simplified — actual set not stored)
  const cumulativeUniqueSkills = new Set<string>();
  // We track unique skills count in progress, but cannot reconstruct set.
  // AchievementTracker uses size check, so we add dummy entries.
  for (let i = 0; i < (skillAch?.progress ?? 0); i++) {
    cumulativeUniqueSkills.add(`_prev_${i}`);
  }

  const runStats = tracker.getStats();

  const pending = tracker.evaluate(
    metaState.achievements,
    cumulativeRuns,
    cumulativeCards,
    cumulativeUniqueSkills,
    stats.livesAtEnd,
    // Chain step A — the hardcore facts travel through the one run-end seam
    // that already carries them (no event duplication, no tracker state).
    { won: stats.won, threatLevel: stats.threatLevel }
  );

  // --- Apply achievement updates ---
  for (const unlock of pending) {
    const ach = metaState.achievements.find((a) => a.id === unlock.id);
    if (!ach || ach.unlocked) continue;

    ach.progress = Math.min(ach.progress + unlock.progressDelta, ach.target);
    if (ach.progress >= ach.target) {
      ach.unlocked = true;
      result.newAchievements.push(ach.id);
      bus.emit("achievement.unlocked", { id: ach.id });

      // Unlock associated module
      const module = getModuleByAchievement(ach.id);
      if (module && !metaState.unlockedModules.includes(module.id)) {
        metaState.unlockedModules.push(module.id);
        result.newModules.push(module.id);
      }
    }
  }

  // --- Update cumulative counters ---
  updateCounter(metaState.achievements, "veteran", 1);
  updateCounter(
    metaState.achievements,
    "card_collector",
    runStats.cardsSelected
  );
  updateCounter(
    metaState.achievements,
    "skill_seeker",
    runStats.uniqueSkillsUsed.size
  );

  // --- Check operator unlocks (based on total runs) ---
  // Bug 2A: the veteran counter was already incremented above (updateCounter
  // "veteran", 1), so its progress is the TRUE completed-run count. The old
  // `+ 1` here double-counted the just-finished run, unlocking each operator one
  // run early. Use the count as-is so unlocks land exactly on the threshold.
  const totalRuns =
    metaState.achievements.find((a) => a.id === "veteran")?.progress ?? 0;
  const operatorCandidates: OperatorId[] = [
    "sentinel-7",
    "warden",
    "ghost",
    "architect",
    "cipher",
  ];
  for (const op of operatorCandidates) {
    if (metaState.unlockedOperators.includes(op)) continue;
    if (totalRuns >= getOperatorUnlockThreshold(op)) {
      metaState.unlockedOperators.push(op);
    }
  }

  // --- Ending of record (S130, Story Bible §4/§8): clean win/lose ---
  // HOLD → "purged"; a fall in Act II/III → "resident" (she keeps the node);
  // an Act-I fall records nothing (she never surfaced — FENRIR simply won).
  const ending: RunEnding | null = stats.won
    ? "purged"
    : stats.wave > GAME.WAVES_ACT_I
    ? "resident"
    : null;
  if (ending && !metaState.storyProgress.endingsReached.includes(ending)) {
    metaState.storyProgress.endingsReached.push(ending);
  }

  // --- Update highscores ---
  const entry: HighscoreEntry = {
    score: stats.score,
    wave: stats.wave,
    operator: stats.operator,
    date: new Date().toISOString().slice(0, 10),
    initials: metaState.playerInitials,
    threatLevel: stats.threatLevel,
  };

  // ── THREAT LEVEL IS PART OF THE SCORE'S IDENTITY (owner, 2026-08-16) ──────
  // The threat contract gives the five levels real, different mechanics, so a
  // sandbox score (0.85x enemy HP) and a fallout score (1.45x) are not
  // comparable. Everything below therefore compares WITHIN one level.
  // Separation, never normalisation — there is no score multiplier anywhere in
  // this file, and there must never be one.

  // "New highscore?" — the caller's own population: same operator AND same
  // level. (Before this round it filtered on operator alone.)
  const previousBest = metaState.highscores
    .filter(
      (h) =>
        h.operator === stats.operator && h.threatLevel === stats.threatLevel
    )
    .reduce((best, h) => Math.max(best, h.score), 0);

  // Top 10 PER LEVEL. The old code kept a flat global top-10, which under the
  // contract would let a fallout run evict a sandbox player's whole board (and
  // vice versa). Each level keeps its own ten; nothing else is dropped.
  metaState.highscores.push(entry);
  metaState.highscores.sort((a, b) => b.score - a.score);
  const perLevel = new Map<string, number>();
  metaState.highscores = metaState.highscores.filter((h) => {
    const n = (perLevel.get(h.threatLevel) ?? 0) + 1;
    perLevel.set(h.threatLevel, n);
    return n <= 10;
  });

  result.isHighscore = stats.score > previousBest;

  // --- Persist ---
  try {
    await manager.save(metaState);
  } catch {
    // Graceful degradation — no crash on save failure
    console.warn("[RunEnd] MetaState save failed — changes not persisted");
  }

  // Additive online submit: local highscore persistence remains authoritative.
  if (leaderboardService) {
    void leaderboardService.submitScore({
      score: stats.score,
      wave: stats.wave,
      operator: stats.operator,
      initials: metaState.playerInitials,
      // The global ranking is separated by level too — same rule, same reason.
      threatLevel: stats.threatLevel,
    });
  }

  bus.emit("meta.updated", {
    newAchievements: result.newAchievements,
    newModules: result.newModules,
    isHighscore: result.isHighscore,
  });

  return result;
}

function updateCounter(
  achievements: Achievement[],
  id: AchievementId,
  delta: number
): void {
  if (delta <= 0) return;
  const ach = achievements.find((a) => a.id === id);
  if (!ach || ach.unlocked) return;
  ach.progress = Math.min(ach.progress + delta, ach.target);
  if (ach.progress >= ach.target) {
    ach.unlocked = true;
  }
}
